import { compactNumber } from "@/utils/number";

import type {
  NameType,
  Payload,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";

import { ChartTooltip } from "./ChartTooltip";

export type ChartTooltipPayload = Payload<ValueType, NameType>;

interface GroupedChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayload[];
  label?: string;
  /** Visual style of the series indicator icon. Defaults to "circle". */
  iconType?: "circle" | "square" | "line";
}

const GroupedChartTooltip = ({
  active,
  payload,
  label,
  iconType = "circle"
}: GroupedChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  const totalValue = payload.reduce(
    (sum, item) => sum + Number(item.value ?? 0),
    0
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-xs max-w-[220px]">
      <p className="font-semibold mb-2 text-txtHeading truncate">{label}</p>
      <div className="flex justify-between items-center mb-2">
        <p className="text-gray-600 mb-2">Total</p>
        <p className="font-medium text-gray-800 flex-shrink-0">
          {compactNumber(totalValue)}
        </p>
      </div>
      <div className="space-y-1">
        {payload.map((entry) => {
          const numValue = Number(entry.value ?? 0);
          const percentValue = totalValue ? (numValue / totalValue) * 100 : 0;

          return (
            <div
              key={entry.dataKey}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {iconType === "line" ? (
                  <svg
                    width="14"
                    height="4"
                    className="shrink-0"
                    aria-hidden="true"
                  >
                    <line
                      x1="0"
                      y1="2"
                      x2="14"
                      y2="2"
                      stroke={entry.stroke ?? entry.color ?? entry.fill}
                      strokeWidth={2}
                    />
                  </svg>
                ) : (
                  <span
                    className={`w-2 h-2 flex-shrink-0${iconType === "circle" ? " rounded-full" : ""}`}
                    style={{ backgroundColor: entry.fill }}
                  />
                )}
                <span className="text-gray-600 truncate">
                  {entry.name ?? entry.dataKey}
                </span>
              </div>
              <span className="font-medium text-gray-800 flex-shrink-0">
                {percentValue > 0 ? `${percentValue.toFixed(1)}%` : "0%"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupedChartTooltip;

export interface ChartTooltipContentProps {
  active?: boolean;
  payload?: ChartTooltipPayload[];
  label?: string;
  isGrouped: boolean;
  totalValue: number;
  iconType?: "circle" | "square" | "line";
}

export const ChartTooltipContent = ({
  active,
  payload,
  label,
  isGrouped,
  totalValue,
  iconType = "circle"
}: ChartTooltipContentProps) =>
  isGrouped ? (
    <GroupedChartTooltip
      active={active}
      payload={payload}
      label={label}
      iconType={iconType}
    />
  ) : (
    <ChartTooltip active={active} payload={payload} totalValue={totalValue} />
  );
